import { useState, useEffect } from 'react';
import {
  MapPinIcon,
  GlobeAltIcon,
  EyeIcon,
  PhotoIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { API_ENDPOINTS } from '../../config/constants';

interface TravelImage {
  id: string;
  url: string;
  title?: string;
  location?: string;
  caption?: string;
}

interface TravelGalleryProps {
  showTitle?: boolean;
  maxImages?: number;
  gridCols?: 'auto' | 2 | 3 | 4;
  className?: string;
}

const TravelGallery = ({
  showTitle = true,
  maxImages,
  gridCols = 'auto',
  className = '',
}: TravelGalleryProps) => {
  const [images, setImages] = useState<TravelImage[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Fetch gallery from backend (published images)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const params = new URLSearchParams();
        params.set('limit', String(maxImages || 24));
        const res = await fetch(
          `${API_ENDPOINTS.gallery}?${params.toString()}`
        );
        const json = await res.json();
        if (!json.success) return;
        const data: TravelImage[] = json.data.map((it: any) => ({
          id: it.id,
          url: (it.cloudinary_url || '').replace(/\/v\d+\//, '/'),
          title: it.title || undefined,
          location: it.location || undefined,
          caption: it.caption || undefined,
        }));
        if (!cancelled) setImages(data);
      } catch {}
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [maxImages]);

  // Auto-rotate images for carousel effect
  useEffect(() => {
    if (images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % images.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [images.length]);

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
  };

  const nextImage = () => {
    setSelectedImageIndex(prev => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImageIndex(prev => (prev - 1 + images.length) % images.length);
  };

  const getGridClass = () => {
    if (gridCols === 'auto') {
      return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
    }
    return `grid-cols-1 sm:grid-cols-${Math.min(gridCols, 2)} lg:grid-cols-${gridCols}`;
  };

  if (images.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <PhotoIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-500">Loading travel gallery...</p>
      </div>
    );
  }

  return (
    <div className={`travel-gallery ${className}`}>
      {showTitle && (
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="w-16 h-1 bg-blue-500 rounded-full mr-4"></div>
            <span className="text-4xl">📸</span>
            <div className="w-16 h-1 bg-blue-500 rounded-full ml-4"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-blue-600 mb-4">
            Travel Gallery
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore our adventures through stunning photography from around the
            world
          </p>
        </div>
      )}

      {/* Featured Image Section */}
      {images.length > 0 && (
        <div className="mb-12">
          <div
            className="relative group cursor-pointer"
            onClick={() => openLightbox(currentImageIndex)}
          >
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <img
                src={images[currentImageIndex].url}
                alt={images[currentImageIndex].title || 'Travel image'}
                className="w-full h-96 lg:h-[500px] object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40"></div>

              {/* Image Overlay Info */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  <h3 className="text-2xl font-bold mb-2 flex items-center">
                    <MapPinIcon className="w-6 h-6 mr-2 text-cyan-400" />
                    {images[currentImageIndex].title || 'Travel Adventure'}
                  </h3>
                  <p className="text-gray-200 mb-3 leading-relaxed">
                    {images[currentImageIndex].caption || ''}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-cyan-300 font-semibold flex items-center">
                      <GlobeAltIcon className="w-4 h-4 mr-1" />
                      {images[currentImageIndex].location || ''}
                    </span>
                    <div className="flex items-center space-x-4 text-sm text-gray-300">
                      <span className="flex items-center">
                        <EyeIcon className="w-4 h-4 mr-1" />
                        Click to view
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Dots */}
              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={e => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentImageIndex
                        ? 'bg-white scale-125'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Grid */}
      <div className={`grid ${getGridClass()} gap-6`}>
        {images.map((image, index) => (
          <div
            key={image.id}
            className="relative group cursor-pointer overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
            onClick={() => openLightbox(index)}
          >
            <img
              src={image.url}
              alt={image.title || 'Travel image'}
              className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="absolute bottom-0 left-0 right-0 p-4 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
              <h4 className="font-bold text-sm mb-1">
                {image.title || 'Travel Adventure'}
              </h4>
              <p className="text-xs text-gray-200 line-clamp-2">
                {image.location || ''}
              </p>
            </div>

            {/* Hover overlay */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <EyeIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {showLightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-6xl max-h-full">
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>

            {/* Navigation arrows */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>

            {/* Image */}
            <img
              src={images[selectedImageIndex].url}
              alt={images[selectedImageIndex].title || 'Travel image'}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />

            {/* Image info */}
            <div className="absolute bottom-4 left-4 right-4 bg-black/70 backdrop-blur-sm rounded-lg p-4 text-white">
              <h3 className="text-xl font-bold mb-2">
                {images[selectedImageIndex].title || 'Travel Adventure'}
              </h3>
              <p className="text-gray-300 mb-2">
                {images[selectedImageIndex].caption || ''}
              </p>
              <p className="text-cyan-300 flex items-center">
                <GlobeAltIcon className="w-4 h-4 mr-1" />
                {images[selectedImageIndex].location || ''}
              </p>
            </div>

            {/* Image counter */}
            <div className="absolute top-4 left-4 bg-black/50 rounded-full px-3 py-1 text-white text-sm">
              {selectedImageIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelGallery;
